import { Injectable } from '@angular/core';

import { SurveyBhUserGrpcContractService } from '@lyra/app/core/api/grpc';
import { ErrorService } from '@lyra/app/core/services/error.service';
import { SettingSelectService } from '@lyra/app/core/store/selectors';
import { BhApi } from '@mark/web-contracts';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs';

import * as actions from '../actions/goals.actions';
import { CHANGE_ROUTE, SET_LOADER } from '../actions/questionnaire.actions';
import { GET_QUESTIONS } from '../actions/questions.actions';
import { INITIALIZE_COMMAND, INITIALIZE_SUCCESS } from '../actions/survey.actions';
import { GoalsSelectService } from '../selectors/goals.selector';
import { MedicalQuestionsSelectService } from '../selectors/medical-questions.selector';
import { QuestionnaireSelectService } from '../selectors/questionnaire.selector';
import { UserSelectService } from '../selectors/user.selector';
import { IQuestionnaireState } from '../states/questionnaire.state';

@Injectable()
export class GoalsEffects {
  getGoals$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.GET_GOALS_COMMAND),
      withLatestFrom(this._settingSelect.getReferrerGoals()),
      switchMap(([, referrerGoals]) =>
        this._surveyBhUserGrpcContractService.getGoals().pipe(
          map((response) => {
            const goals = (response?.Goals ?? []).map((goal) => ({
              Id: goal.Id,
              Title: goal.Title,
              Description: goal.Description,
              Priority: goal.Priority,
              Type: goal.Type,
              WidgetTitle: goal.WidgetTitle,
              ImageUrl: goal.Image?.Url,
              WidgetImageUrl: goal.WidgetImage?.Url,
              Childrens: goal.Childrens?.map((child) => ({
                Id: child.Id,
                Title: child.Title,
                Description: child.Description,
                Priority: child.Priority,
                Type: child.Type,
                WidgetTitle: child.WidgetTitle,
                ImageUrl: child.Image?.Url,
                WidgetImageUrl: child.WidgetImage?.Url,
              })),
            }));

            if (referrerGoals) {
              const goalsWithReferrer = goals.map((goal) => ({
                ...goal,
                selected: referrerGoals.map((x) => x.toLowerCase()).includes(goal.Title!.toLowerCase()),
              }));
              this._store$.dispatch(actions.GET_GOALS_SUCCESS({ goals: goalsWithReferrer }));
              return actions.SET_GOALS_COMMAND();
            } else {
              return actions.GET_GOALS_SUCCESS({ goals });
            }
          }),
          catchError((response) =>
            this._errorService.handleApiError(response, 'SurveyBhUserWebGrpcContractClient.GetGoals'),
          ),
        ),
      ),
    ),
  );

  setGoals$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.SET_GOALS_COMMAND),
      withLatestFrom(this._goalsSelect.selectedGoalsIds()),
      map(([, goalIds]) => INITIALIZE_COMMAND({ goalIds })),
    ),
  );

  getGoalsSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.GET_GOALS_COMMAND),
        withLatestFrom(this._settingSelect.getReferrerGoals()),
        tap(([, referrerGoals]) => {
          if (!referrerGoals) {
            this._store$.dispatch(CHANGE_ROUTE({ route: 'goals' }));
          } else {
            this._store$.dispatch(SET_LOADER({ value: true }));
          }
        }),
      ),
    { dispatch: false },
  );

  goalsInfoContinue$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(INITIALIZE_SUCCESS),
        withLatestFrom(this._medicalQuestionsSelect.hasQuestions(), this._userSelect.getMainUserData()),
        tap(([, hasMedicalQuestions, { GenderType }]) => {
          if (GenderType === BhApi.GenderType.GenderType_Female) {
            this._store$.dispatch(CHANGE_ROUTE({ route: 'woman-health' }));
            this._store$.dispatch(SET_LOADER({ value: false }));
            return;
          }
          if (hasMedicalQuestions) {
            if (GenderType === BhApi.GenderType.GenderType_Male) {
              this._store$.dispatch(CHANGE_ROUTE({ route: 'medical-questions' }));
            }
          } else {
            this._store$.dispatch(CHANGE_ROUTE({ route: 'questions' }));
          }
          this._store$.dispatch(SET_LOADER({ value: false }));
          this._store$.dispatch(GET_QUESTIONS());
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private _store$: Store<IQuestionnaireState>,
    private _actions$: Actions,
    private _errorService: ErrorService,
    private _surveyBhUserGrpcContractService: SurveyBhUserGrpcContractService,
    private _userSelect: UserSelectService,
    private _questionnaireSelect: QuestionnaireSelectService,
    private _goalsSelect: GoalsSelectService,
    private _medicalQuestionsSelect: MedicalQuestionsSelectService,
    private _settingSelect: SettingSelectService,
  ) {}
}
